import {writePersistedLiveUpdateContext} from "../helpers/live-update-persist";

const LIVE_UPDATE_MUTATION_BRIDGE_EVENT = 'wl_live_update_mutation_bridge';
const liveUpdateMutationBridgeStateKey = '__wlLiveUpdateMutationBridgeState';
const fallbackTriggerThrottleMs = 250;
const coalescedDispatchWindowMs = 75;

function sanitizePostType(postType) {
    const value = String(postType || '').trim().toLowerCase();
    return value.replace(/[^a-z0-9_:-]+/g, '').slice(0, 64);
}

function sanitizeRefreshTraceID(refreshTraceID) {
    return String(refreshTraceID || '').replace(/[^a-zA-Z0-9:_-]+/g, '').slice(0, 96);
}

function normalizePositiveInt(value) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0;
    }

    return parsed;
}

function normalizeTriggerPayload(payload = {}) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return null;
    }

    const postType = sanitizePostType(payload.post_type);
    if (!postType) {
        return null;
    }

    const context = {
        post_type: postType,
        update_seq: normalizePositiveInt(payload.update_seq),
        task_id: normalizePositiveInt(payload.task_id),
        observed_at_ms: normalizePositiveInt(payload.observed_at_ms),
        refresh_trace_id: sanitizeRefreshTraceID(payload.refresh_trace_id),
    };

    context.has_strict_context = context.update_seq > 0;
    return context;
}

function compareTriggerContexts(a = null, b = null) {
    if (!a && !b) return 0;
    if (!a) return -1;
    if (!b) return 1;

    const aSeq = normalizePositiveInt(a.update_seq);
    const bSeq = normalizePositiveInt(b.update_seq);
    if (aSeq !== bSeq) return aSeq - bSeq;

    const aObservedAtMs = normalizePositiveInt(a.observed_at_ms);
    const bObservedAtMs = normalizePositiveInt(b.observed_at_ms);
    if (aObservedAtMs !== bObservedAtMs) return aObservedAtMs - bObservedAtMs;

    const aTaskID = normalizePositiveInt(a.task_id);
    const bTaskID = normalizePositiveInt(b.task_id);
    if (aTaskID > 0 && bTaskID > 0 && aTaskID !== bTaskID) return aTaskID - bTaskID;

    return 0;
}


function logBridgeDebug(action, reason, context = null, meta = {}) {
    return;
}

function queueCoalescedDispatch(postTypeState, context) {
    const currentPendingDispatch = postTypeState.pendingDispatch || null;
    const pendingCmp = compareTriggerContexts(context, currentPendingDispatch);
    if (pendingCmp < 0) {
        logBridgeDebug('ignored', 'older_than_pending_dispatch', context, {
            pending_dispatch: currentPendingDispatch,
        });
        return false;
    }

    postTypeState.pendingDispatch = context;
    if (postTypeState.pendingDispatchTimerID) {
        logBridgeDebug('triggered', 'coalesced_pending_update', context, {
            dispatch_window_ms: coalescedDispatchWindowMs,
        });
        return true;
    }

    postTypeState.pendingDispatchTimerID = setTimeout(() => {
        const dispatchContext = postTypeState.pendingDispatch || null;
        postTypeState.pendingDispatch = null;
        postTypeState.pendingDispatchTimerID = null;
        if (!dispatchContext) {
            return;
        }

        document.dispatchEvent(new CustomEvent(LIVE_UPDATE_MUTATION_BRIDGE_EVENT, {detail: dispatchContext}));
        logBridgeDebug('triggered', 'dispatched', dispatchContext, {
            dispatch_window_ms: coalescedDispatchWindowMs,
        });
    }, coalescedDispatchWindowMs);

    logBridgeDebug('triggered', 'queued_dispatch', context, {
        dispatch_window_ms: coalescedDispatchWindowMs,
    });
    return true;
}

function getBridgeState() {
    if (typeof window === 'undefined') {
        return {byPostType: {}};
    }

    if (!window[liveUpdateMutationBridgeStateKey] || typeof window[liveUpdateMutationBridgeStateKey] !== 'object') {
        window[liveUpdateMutationBridgeStateKey] = {byPostType: {}};
    }

    return window[liveUpdateMutationBridgeStateKey];
}

function getPostTypeState(postType) {
    const bridgeState = getBridgeState();
    if (!bridgeState.byPostType || typeof bridgeState.byPostType !== 'object') {
        bridgeState.byPostType = {};
    }
    if (!bridgeState.byPostType[postType] || typeof bridgeState.byPostType[postType] !== 'object') {
        bridgeState.byPostType[postType] = {
            lastStrictContext: null,
            lastFallbackTriggeredAtMs: 0,
            lastFallbackContext: null,
            pendingDispatch: null,
            pendingDispatchTimerID: null,
        };
    }

    return bridgeState.byPostType[postType];
}

export class LiveUpdateMutationBridge {
    trigger(payload = {}) {
        const context = normalizeTriggerPayload(payload);
        if (!context) {
            logBridgeDebug('ignored', 'invalid_payload', payload);
            return false;
        }

        const postTypeState = getPostTypeState(context.post_type);
        if (context.has_strict_context) {
            writePersistedLiveUpdateContext(context);
            const strictCmp = compareTriggerContexts(context, postTypeState.lastStrictContext);
            if (strictCmp <= 0) {
                logBridgeDebug('ignored', strictCmp === 0 ? 'duplicate' : 'regressive', context, {
                    last_strict_context: postTypeState.lastStrictContext,
                });
                return false;
            }
            postTypeState.lastStrictContext = context;
        } else {
            const previousFallbackMs = normalizePositiveInt(postTypeState.lastFallbackTriggeredAtMs);
            const nowMs = Math.max(Date.now(), previousFallbackMs);
            if (nowMs - previousFallbackMs < fallbackTriggerThrottleMs) {
                postTypeState.lastFallbackContext = context;
                logBridgeDebug('ignored', 'fallback_throttled', context, {
                    last_fallback_triggered_at_ms: previousFallbackMs,
                    throttle_window_ms: fallbackTriggerThrottleMs,
                });
                return false;
            }
            postTypeState.lastFallbackTriggeredAtMs = nowMs;
            postTypeState.lastFallbackContext = context;
        }

        return queueCoalescedDispatch(postTypeState, context);
    }
}

export {LIVE_UPDATE_MUTATION_BRIDGE_EVENT};
export const liveUpdateMutationBridge = new LiveUpdateMutationBridge();
